import type { TFunction } from "i18next"
import React, { createContext, useContext, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { useAppContext } from "@contexts/app_context"
import {
  getSettings,
  listAnnouncements,
  listCategories
} from "@domain/changelog"
import useToggle from "@hooks/use_toggle"
import {
  Changelog,
  ChangelogCategory,
  ChangelogStatus,
  ChangelogSettings
} from "@models/changelog"
import { useQuery } from "@tanstack/react-query"
import i18next from "@utils/localization"

export type ChangelogSection =
  | "announcements"
  | "categories"
  | "subscribers"
  | "deliveries"
  | "settings"

export type StatusFilter = "all" | ChangelogStatus

type ChangelogPageContextProps = {
  translate: TFunction<"translation", undefined>

  activeSection: ChangelogSection
  setActiveSection: (section: ChangelogSection) => void

  // announcements
  announcements: Changelog[]
  announcementsLoading: boolean
  refetchAnnouncements: () => void
  statusFilter: StatusFilter
  setStatusFilter: (f: StatusFilter) => void
  search: string
  setSearch: (s: string) => void

  // categories
  categories: ChangelogCategory[]
  categoriesLoading: boolean
  refetchCategories: () => void

  // settings
  settings?: ChangelogSettings
  settingsLoading: boolean
  refetchSettings: () => void

  // announcement create/edit drawer
  isAnnouncementDrawerOpen: boolean
  editingAnnouncement?: Changelog
  openCreateAnnouncement: () => void
  openEditAnnouncement: (a: Changelog) => void
  closeAnnouncementDrawer: () => void

  // send-notification modal
  isSendModalOpen: boolean
  sendTarget?: Changelog
  openSendModal: (a: Changelog) => void
  closeSendModal: () => void

  // preview drawer
  isPreviewOpen: boolean
  togglePreview: () => void

  // announcement feedback detail drawer
  detailTarget?: Changelog
  isDetailOpen: boolean
  openDetail: (a: Changelog) => void
  closeDetail: () => void

  // category create/edit modal
  isCategoryModalOpen: boolean
  editingCategory?: ChangelogCategory
  openCreateCategory: () => void
  openEditCategory: (c: ChangelogCategory) => void
  closeCategoryModal: () => void

  // category delete-reassign modal
  isDeleteCategoryOpen: boolean
  deleteCategoryTarget?: ChangelogCategory
  openDeleteCategory: (c: ChangelogCategory) => void
  closeDeleteCategory: () => void
}

const noop = () => undefined

const defaultValues: ChangelogPageContextProps = {
  translate: i18next.t,
  activeSection: "announcements",
  setActiveSection: noop,
  announcements: [],
  announcementsLoading: true,
  refetchAnnouncements: noop,
  statusFilter: "all",
  setStatusFilter: noop,
  search: "",
  setSearch: noop,
  categories: [],
  categoriesLoading: true,
  refetchCategories: noop,
  settingsLoading: true,
  refetchSettings: noop,
  isAnnouncementDrawerOpen: false,
  openCreateAnnouncement: noop,
  openEditAnnouncement: noop,
  closeAnnouncementDrawer: noop,
  isSendModalOpen: false,
  openSendModal: noop,
  closeSendModal: noop,
  isPreviewOpen: false,
  togglePreview: noop,
  isDetailOpen: false,
  openDetail: noop,
  closeDetail: noop,
  isCategoryModalOpen: false,
  openCreateCategory: noop,
  openEditCategory: noop,
  closeCategoryModal: noop,
  isDeleteCategoryOpen: false,
  openDeleteCategory: noop,
  closeDeleteCategory: noop
}

const ChangelogPageContext = createContext(defaultValues)

export function ChangelogPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate } = useAppContext()

  // Section is driven by the URL (/changelog/:section) so refreshes keep the tab.
  const navigate = useNavigate()
  const { section } = useParams<{ section?: string }>()
  const sections: ChangelogSection[] = [
    "announcements",
    "categories",
    "subscribers",
    "deliveries",
    "settings"
  ]
  const activeSection: ChangelogSection = sections.includes(
    section as ChangelogSection
  )
    ? (section as ChangelogSection)
    : "announcements"
  const setActiveSection = (next: ChangelogSection) =>
    navigate(`/changelog/${next}`)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
  const [search, setSearch] = useState("")

  const [isAnnouncementDrawerOpen, toggleAnnouncementDrawer] = useToggle(false)
  const [editingAnnouncement, setEditingAnnouncement] = useState<Changelog>()

  const [isSendModalOpen, toggleSendModal] = useToggle(false)
  const [sendTarget, setSendTarget] = useState<Changelog>()

  const [isPreviewOpen, togglePreview] = useToggle(false)

  const [isDetailOpen, toggleDetail] = useToggle(false)
  const [detailTarget, setDetailTarget] = useState<Changelog>()

  const [isCategoryModalOpen, toggleCategoryModal] = useToggle(false)
  const [editingCategory, setEditingCategory] = useState<ChangelogCategory>()

  const [isDeleteCategoryOpen, toggleDeleteCategory] = useToggle(false)
  const [deleteCategoryTarget, setDeleteCategoryTarget] =
    useState<ChangelogCategory>()

  // Fetch all announcements; status/search filtering happens client-side so the
  // filter-tab counts (Tümü / Yayında / …) stay accurate.
  const announcementsQuery = useQuery({
    queryKey: ["changelog-announcements"],
    queryFn: async () => {
      const response = await listAnnouncements({ pageNumber: 1, pageSize: 200 })
      return response.data?.docs ?? []
    }
  })

  const categoriesQuery = useQuery({
    queryKey: ["changelog-categories"],
    queryFn: async () => (await listCategories()).data ?? []
  })

  const settingsQuery = useQuery({
    queryKey: ["changelog-settings"],
    queryFn: async () => (await getSettings()).data
  })

  const value = useMemo<ChangelogPageContextProps>(
    () => ({
      translate,
      activeSection,
      setActiveSection,
      announcements: announcementsQuery.data ?? [],
      announcementsLoading: announcementsQuery.isLoading,
      refetchAnnouncements: () => void announcementsQuery.refetch(),
      statusFilter,
      setStatusFilter,
      search,
      setSearch,
      categories: categoriesQuery.data ?? [],
      categoriesLoading: categoriesQuery.isLoading,
      refetchCategories: () => void categoriesQuery.refetch(),
      settings: settingsQuery.data ?? undefined,
      settingsLoading: settingsQuery.isLoading,
      refetchSettings: () => void settingsQuery.refetch(),
      isAnnouncementDrawerOpen,
      editingAnnouncement,
      openCreateAnnouncement: () => {
        setEditingAnnouncement(undefined)
        if (!isAnnouncementDrawerOpen) {
          toggleAnnouncementDrawer()
        }
      },
      openEditAnnouncement: (a: Changelog) => {
        setEditingAnnouncement(a)
        if (!isAnnouncementDrawerOpen) {
          toggleAnnouncementDrawer()
        }
      },
      closeAnnouncementDrawer: () => {
        if (isAnnouncementDrawerOpen) {
          toggleAnnouncementDrawer()
        }
        setEditingAnnouncement(undefined)
      },
      isSendModalOpen,
      sendTarget,
      openSendModal: (a: Changelog) => {
        setSendTarget(a)
        if (!isSendModalOpen) {
          toggleSendModal()
        }
      },
      closeSendModal: () => {
        if (isSendModalOpen) {
          toggleSendModal()
        }
        setSendTarget(undefined)
      },
      isPreviewOpen,
      togglePreview,
      detailTarget,
      isDetailOpen,
      openDetail: (a: Changelog) => {
        setDetailTarget(a)
        if (!isDetailOpen) {
          toggleDetail()
        }
      },
      closeDetail: () => {
        if (isDetailOpen) {
          toggleDetail()
        }
        setDetailTarget(undefined)
      },
      isCategoryModalOpen,
      editingCategory,
      openCreateCategory: () => {
        setEditingCategory(undefined)
        if (!isCategoryModalOpen) {
          toggleCategoryModal()
        }
      },
      openEditCategory: (c: ChangelogCategory) => {
        setEditingCategory(c)
        if (!isCategoryModalOpen) {
          toggleCategoryModal()
        }
      },
      closeCategoryModal: () => {
        if (isCategoryModalOpen) {
          toggleCategoryModal()
        }
        setEditingCategory(undefined)
      },
      isDeleteCategoryOpen,
      deleteCategoryTarget,
      openDeleteCategory: (c: ChangelogCategory) => {
        setDeleteCategoryTarget(c)
        if (!isDeleteCategoryOpen) {
          toggleDeleteCategory()
        }
      },
      closeDeleteCategory: () => {
        if (isDeleteCategoryOpen) {
          toggleDeleteCategory()
        }
        setDeleteCategoryTarget(undefined)
      }
    }),
    [
      translate,
      activeSection,
      announcementsQuery.data,
      announcementsQuery.isLoading,
      statusFilter,
      search,
      categoriesQuery.data,
      categoriesQuery.isLoading,
      settingsQuery.data,
      settingsQuery.isLoading,
      isAnnouncementDrawerOpen,
      editingAnnouncement,
      isSendModalOpen,
      sendTarget,
      isPreviewOpen,
      detailTarget,
      isDetailOpen,
      isCategoryModalOpen,
      editingCategory,
      isDeleteCategoryOpen,
      deleteCategoryTarget
    ]
  )

  return (
    <ChangelogPageContext.Provider value={value}>
      {props.children}
    </ChangelogPageContext.Provider>
  )
}

export function useChangelogPageContext() {
  const context = useContext(ChangelogPageContext)
  if (context === undefined) {
    throw new Error("Changelog Page Context Error")
  }
  return context
}
